import {
  Box,
  Button,
  Divider,
  Flex,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Image } from "../../components";
import { MdOutlineAccessTime } from "react-icons/md";
import { MovieCardProps } from "../../@types";
import { SHOW_BOOKING_CLOSED } from "../../constant/alertMessage";
import { getShowStatusColorScheme } from "../../constant/enums";
import { useRouter } from "../../hooks/useRouter";
import GradeAWarn from "../WarningModules/gradeAWarn";
import { useState } from "react";

function MovieCardAsGallery({
  poster,
  title,
  duration,
  grade,
  genre,
  releaseDate,
  movieShowTimes,
  showAudiName,
}: MovieCardProps) {
  const router = useRouter();
  const [modelroute, setModalRoute] = useState<string>("");
  const {
    isOpen: messageisOpen,
    onOpen: messageonOpen,
    onClose: messageonClose,
  } = useDisclosure();
  return (
    <Flex
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor={"primary.300"}
      overflow="hidden"
      w={{
        base: "100%",
        md: "calc(50% - 1.4rem)",
      }}
      maxW="100%"
      h={{
        base: "",
        md: "333px",
      }}
      mt={{
        base: "2",
        md: "4",
      }}
      transition="all 0.3s ease-in-out"
      _hover={{
        transform: "translateY(-4px)",
        boxShadow: "xl",
      }}
      cursor="pointer"
    >
      <Box
        w={{
          base: "100%",
          md: "40%",
        }}
        position="relative"
        overflow="hidden"
      >
        <Image
          src={poster}
          alt="Movie Poster"
          objectFit="fill"
          h="100%"
          w="100%"
          transition="transform 0.4s ease-in-out"
          _hover={{
            transform: "scale(1.05)",
          }}
        />
      </Box>

      <Box
        w={{
          base: "100%",
          md: "60%",
        }}
        p={{
          base: "3",
          md: "4",
        }}
        bg="gray.50"
      >
        <VStack align="start" spacing="2">
          <Text
            fontSize={{
              base: "lg",
              md: "xl",
            }}
            fontWeight="bold"
            noOfLines={2}
            transition="color 0.2s ease"
            _hover={{
              color: "primary.500",
            }}
          >
            {title}
          </Text>
          <Divider
            border="1px solid #CBCBCB"
            sx={{
              rounded: "full",
            }}
          />
          <Flex
            fontSize={{
              base: "12px",
              md: "14px",
            }}
            fontWeight={"400"}
            color={"surface.500"}
            display={"flex"}
            gap={2}
            alignItems={"center"}
            transition="transform 0.2s ease"
            _hover={{
              transform: "translateX(4px)",
            }}
          >
            <MdOutlineAccessTime fontSize={"16px"} />
            <Text>{duration}</Text>
          </Flex>
          <Flex
            fontSize={{
              base: "12px",
              md: "14px",
            }}
            fontWeight={"400"}
            color={"surface.500"}
            display={"flex"}
            gap={2}
            alignItems={"center"}
            transition="transform 0.2s ease"
            _hover={{
              transform: "translateX(4px)",
            }}
          >
            <MdOutlineAccessTime fontSize={"16px"} />
            <Text>{grade}</Text>
          </Flex>
          <Flex
            fontSize={{
              base: "12px",
              md: "14px",
            }}
            fontWeight={"400"}
            color={"surface.500"}
            display={"flex"}
            gap={2}
            alignItems={"center"}
            transition="transform 0.2s ease"
            _hover={{
              transform: "translateX(4px)",
            }}
          >
            <MdOutlineAccessTime fontSize={"16px"} />
            <Text>{genre}</Text>
          </Flex>
          <Flex
            fontSize={{
              base: "12px",
              md: "14px",
            }}
            fontWeight={"400"}
            color={"surface.500"}
            display={"flex"}
            gap={2}
            alignItems={"center"}
            transition="transform 0.2s ease"
            _hover={{
              transform: "translateX(4px)",
            }}
          >
            <MdOutlineAccessTime fontSize={"16px"} />
            <Text>Release Date: {releaseDate}</Text>
          </Flex>

          {movieShowTimes && movieShowTimes.length > 0 && (
            <Divider
              border="1px solid #CBCBCB"
              sx={{
                rounded: "full",
              }}
            />
          )}
        </VStack>

        <Flex
          wrap="wrap"
          mt="3"
          p="1"
          gap="2"
          maxH={{
            base: "auto",
            md: "80px",
          }}
          overflowY="auto"
          sx={{
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "gray.100",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "primary.300",
              borderRadius: "full",
            },
          }}
        >
          {movieShowTimes?.map(
            (
              {
                start_time,
                available,
                viewable,
                showLink,
                lock_web,
                show_status,
                auditorium,
              },
              index,
            ) => (
              <Box
                key={`${start_time}-${index}`}
                opacity={0}
                animation={`fadeInUp 0.3s ease-out ${index * 0.05}s forwards`}
                sx={{
                  "@keyframes fadeInUp": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(10px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <Button
                  colorScheme={getShowStatusColorScheme(show_status)}
                  variant="outline"
                  size="sm"
                  color="black"
                  // Locked and already-started shows stay clickable: the
                  // seat layout opens read-only.
                  isDisabled={!(viewable ?? available)}
                  opacity={
                    lock_web && (viewable ?? available) ? 0.75 : undefined
                  }
                  title={
                    lock_web && (viewable ?? available)
                      ? SHOW_BOOKING_CLOSED
                      : undefined
                  }
                  onClick={() => {
                    if (grade === "A") {
                      messageonOpen();
                      setModalRoute(showLink);
                    } else {
                      router.push(showLink);
                    }
                  }}
                  borderColor={`${getShowStatusColorScheme(show_status)}.500`}
                  fontWeight={"normal"}
                  h="auto"
                  py="0.35rem"
                  transition="all 0.2s ease-in-out"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "md",
                  }}
                  _active={{
                    transform: "scale(0.95)",
                  }}
                >
                  <Flex direction="column" align="center" gap="0">
                    {showAudiName && auditorium?.name && (
                      <Text as="span" fontSize="xs" fontWeight="normal" opacity={0.8} lineHeight="1.3">
                        {auditorium.name}
                      </Text>
                    )}
                    <Text as="span" lineHeight="1.4">{start_time}</Text>
                  </Flex>
                </Button>
              </Box>
            ),
          )}
        </Flex>
      </Box>
      <GradeAWarn
        isOpen={messageisOpen}
        onClose={messageonClose}
        onProceed={() => router.push(modelroute)}
        modalKey={"warning_module"}
      />
    </Flex>
  );
}

export default MovieCardAsGallery;
